import { z } from 'zod';
import { Appearance, MyLifeReply, type Appearance as AppearanceValue } from './character';
import { USERNAME_MESSAGES, checkUsername, containsBlockedWord } from './profile';

// The player's profile screen: their names, their settings, and paid changes to their look.

// --- settings: the player's own choices, kept on the server so they follow them to any device ---
export const PlayerSettings = z
  .object({
    // lighter graphics: fewer pixels and frames, for older phones and mobile data
    dataSaver: z.boolean(),
    // chat: let people you message see when you have read theirs (and see theirs in return)
    readReceipts: z.boolean(),
    // a short sound with a new message's pop-up
    sounds: z.boolean().default(true),
  })
  .strict();
export type PlayerSettings = z.infer<typeof PlayerSettings>;
export const DEFAULT_PLAYER_SETTINGS: PlayerSettings = { dataSaver: false, readReceipts: true, sounds: true };
export const PlayerSettingsReply = z.object({ settings: PlayerSettings });
export type PlayerSettingsReply = z.infer<typeof PlayerSettingsReply>;

// --- the account part of the profile ---
export const DisplayName = z
  .string()
  .trim()
  .min(2, 'Use at least 2 characters')
  .max(30, 'Use 30 characters or fewer')
  .regex(/^\p{L}[\p{L}\p{N}' .-]*$/u, 'Use letters, numbers, spaces, hyphens, dots or apostrophes')
  .refine((name) => !containsBlockedWord(name), 'Please choose a different name');
export const DisplayNameChange = z.object({ displayName: DisplayName }).strict();
export type DisplayNameChange = z.infer<typeof DisplayNameChange>;
export const UsernameChange = z
  .object({
    username: z.string().refine((value) => checkUsername(value).ok, USERNAME_MESSAGES.format),
  })
  .strict();
export type UsernameChange = z.infer<typeof UsernameChange>;
export const AccountView = z.object({
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  // when the @username may next be changed (null: now)
  usernameChangeFrom: z.string().nullable(),
  joinedAt: z.string(),
});
export type AccountView = z.infer<typeof AccountView>;
export const AccountReply = z.object({ account: AccountView });
export type AccountReply = z.infer<typeof AccountReply>;

// --- changing the look: who you are (body, build, skin) is free to correct; styling costs what it would in
// London (a barber, a dye job, new clothes), paid from the wallet through the ledger ---
export const LOOK_PRICES_PENCE = {
  hair: 1800,
  hairColour: 5500,
  facialHair: 1200,
  outfit: 4500,
  accessories: 2000,
} as const;
export type LookPart = keyof typeof LOOK_PRICES_PENCE;
export const LOOK_PART_LABELS: Record<LookPart, string> = {
  hair: 'Haircut',
  hairColour: 'Hair colour',
  facialHair: 'Beard trim',
  outfit: 'New outfit',
  accessories: 'Accessories',
};
// what changing from one look to another costs, part by part
export function lookChangeCost(from: AppearanceValue, to: AppearanceValue) {
  const parts = (Object.keys(LOOK_PRICES_PENCE) as LookPart[]).filter((part) =>
    part === 'accessories'
      ? [...from.accessories].sort().join(',') !== [...to.accessories].sort().join(',')
      : from[part] !== to[part],
  );
  return { parts, totalPence: parts.reduce((sum, part) => sum + LOOK_PRICES_PENCE[part], 0) };
}
export const LookChange = z.object({ appearance: Appearance }).strict();
export type LookChange = z.infer<typeof LookChange>;
export const LookChangeReply = MyLifeReply.extend({ message: z.string() });
export type LookChangeReply = z.infer<typeof LookChangeReply>;
